import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDeletedClasses, restoreClass, permanentlyDeleteClass } from "../api/Classes";
import { Class } from "../types";
import { useAuth } from '../useAuth';
import { useTimezone } from '../hooks/useTimezone';
import { usePageTitle } from '../hooks/usePageTitle';

function DeletedClasses() {
  usePageTitle('Deleted Classes');
  const navigate = useNavigate();
  const { formatDate } = useTimezone();
  const { user } = useAuth();

  const [classes, setClasses] = useState<Class[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState<string | null>(null);
  const [permanentTarget, setPermanentTarget] = useState<Class | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      loadDeletedClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadDeletedClasses = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getDeletedClasses();
      setClasses(data);
    } catch (err: any) {
      console.error('Failed to load deleted classes:', err);
      setError('Failed to load deleted classes.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (classId: string) => {
    if (!window.confirm('Restore this class? It will reappear in your active classes list.')) return;

    setRestoring(classId);
    try {
      await restoreClass(classId);
      setClasses(classes.filter(c => c._id !== classId));
    } catch (err: any) {
      console.error('Failed to restore class:', err);
      alert('Failed to restore class: ' + (err.response?.data?.error || err.message));
    } finally {
      setRestoring(null);
    }
  };

  const openPermanentDeleteModal = (cls: Class) => {
    setPermanentTarget(cls);
    setConfirmText('');
  };

  const closePermanentDeleteModal = () => {
    setPermanentTarget(null);
    setConfirmText('');
  };

  const handlePermanentDelete = async () => {
    if (!permanentTarget || confirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await permanentlyDeleteClass(permanentTarget._id);
      setClasses(classes.filter(c => c._id !== permanentTarget._id));
      closePermanentDeleteModal();
    } catch (err: any) {
      console.error('Failed to permanently delete class:', err);
      alert('Failed to permanently delete class: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Deleted Classes</h2>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/classes')}>
            <i className="bi bi-arrow-left me-2"></i>Back to Classes
          </button>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const filtered = classes.filter(c => c.classname?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Deleted Classes</h2>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/classes')}>
          <i className="bi bi-arrow-left me-2"></i>Back to Classes
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search deleted classes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="card-header bg-dark text-white">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-trash me-2"></i>Deleted Classes
            </span>
            <span className="small opacity-75">
              {search
                ? `${filtered.length} of ${classes.length}`
                : classes.length} {classes.length === 1 ? 'class' : 'classes'}
            </span>
          </div>
        </div>
        <div className="card-body p-0">
          {classes.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <i className="bi bi-trash display-1 d-block mb-3 opacity-25"></i>
              <p className="mb-0">No deleted classes.</p>
              <small>Classes you delete are kept here — nothing is lost until you permanently delete it.</small>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <p className="mb-0">No deleted classes match "{search}".</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Date</th>
                    <th scope="col">CEC</th>
                    <th scope="col">Status</th>
                    <th scope="col">Deleted</th>
                    <th scope="col" style={{ width: '220px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((cls: Class) => (
                    <tr key={cls._id}>
                      <td title={cls.classname} style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cls.classname}
                        {cls.docpath && <span className="badge bg-info text-white ms-2" title="Has certificate">▣</span>}
                      </td>
                      <td>{formatDate(cls.classdate)}</td>
                      <td>{cls.classLU}</td>
                      <td>
                        {cls.took ? (
                          <span className="badge bg-success">Completed</span>
                        ) : (
                          <span className="badge bg-warning text-dark">Not Completed</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {cls.deletedAt ? formatDate(cls.deletedAt) : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleRestore(cls._id)}
                            disabled={restoring === cls._id}
                          >
                            {restoring === cls._id ? 'Restoring...' : 'Restore'}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openPermanentDeleteModal(cls)}
                          >
                            Delete Permanently
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="alert alert-info mt-4">
        <i className="bi bi-info-circle me-2"></i>
        <strong>About Deleted Classes:</strong> When you delete a class, it's moved here instead of being removed immediately.
        You can restore it back to your active list, or permanently delete it — which cannot be undone.
      </div>

      {permanentTarget && (
        <div
          className="modal show d-block"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={closePermanentDeleteModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>Permanently Delete Class
                </h5>
                <button type="button" className="btn-close" onClick={closePermanentDeleteModal}></button>
              </div>
              <div className="modal-body">
                <p>
                  This will permanently and irreversibly delete <strong>{permanentTarget.classname}</strong>.
                  It cannot be restored after this.
                </p>
                <p className="mb-2">Type <code>DELETE</code> to confirm:</p>
                <input
                  type="text"
                  className="form-control"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={closePermanentDeleteModal}>Cancel</button>
                <button
                  className="btn btn-danger"
                  disabled={confirmText !== 'DELETE' || deleting}
                  onClick={handlePermanentDelete}
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeletedClasses;
